import {z} from 'zod';
import {
	editRemovesFromFilter,
	getDefaultFilters,
	makeOptionalPropsNullable,
	type EditRemovesFromFilterParams,
	type GetDefaultFiltersParams,
} from '@netx/core-next/schema/utils';
import {typedEntries} from '@netx/core-next/utils/object';
import {UserSchema, type IUser} from './userSchema';

const userShape = UserSchema.shape;

// We need to be matching what the filter will be (not what the keys on the
// user are - we need to write a translation util between the two).
// The UserFilterDTO on the backend is:
// 'firstName'
// 'lastName'
// 'email'
// 'username',
// 'userType',

export const UserFilterSchema = makeOptionalPropsNullable(
	z.object({
		firstName: userShape.name1.optional(),
		lastName: userShape.name3.optional(),
		email: userShape.email.optional(),
		username: userShape.login.optional(),
		userType: userShape.type.optional(),
		disabled: userShape.disabled.optional(),
		groupIds: userShape.groupIds.optional(),
	}),
);

export type UsersFilter = z.infer<typeof UserFilterSchema>;

export const UserFilterableField = UserFilterSchema.keyof().enum;

export const USER_FILTERABLE_FIELDS = Object.values(UserFilterableField);

/** will return the filters passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getUsersDefaultFilters = (
	params?: Omit<GetDefaultFiltersParams<UsersFilter>, 'filterableFields'>,
): UsersFilter =>
	getDefaultFilters({
		...params,
		filterableFields: USER_FILTERABLE_FIELDS,
	});

// const filterToUserKeyMap: Record<keyof UsersFilter, keyof IUser> = {
// 	firstName: UserField.name1,
// 	lastName: UserField.name3,
// 	email: UserField.email,
// 	username: UserField.login,
// 	userType: UserField.type,
// 	disabled: UserField.disabled,
// 	groupIds: UserField.groupIds,
// };

enum UserFilterToUserKeyMap {
	firstName = 'name1',
	lastName = 'name3',
	email = 'email',
	username = 'login',
	userType = 'type',
	disabled = 'disabled',
	groupIds = 'groupIds',
}

/** given the original data, the new data, and the current filters, determine if the record should still be included in the filtered list */
export const userEditRemovesFromFilter = (
	params: Omit<EditRemovesFromFilterParams<IUser, UsersFilter>, 'schema'>,
) => {
	const mappedFilters: Partial<IUser> = {};

	typedEntries(params.filters).forEach(([key]) => {
		const userKey = UserFilterToUserKeyMap[key];
		// @ts-expect-error - we need to be able to index with the filter keys, which are different than the user keys, so we need to do a translation step - this is the translation step
		if (userKey) mappedFilters[userKey] = params.filters[key];
	});

	return editRemovesFromFilter({
		...params,
		filters: mappedFilters,
		schema: UserSchema,
	});
};
